/**
 * 설정 › 사용자 유형 (관리자) — beginner · advanced · expert 를 사람에게 붙인다(2026-10-29).
 *
 * 처음 보는 사람은 beginner 이고(서버의 fail-closed), 이 장치 전에 쓰던 사람은 advanced 로 한 번 깔렸다.
 * 관리자와 유료 티어는 늘 expert 로 보여 고를 수 없다. 자동 승급·투표는 아직 없다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiRefreshCw } from 'react-icons/fi';
import { listUserGrades, setUserGrade } from '../services/userGrade';
import { formatApiError } from '../utils/formatters';
import { storedTime } from '../utils/storedTime';

const GRADES = ['beginner', 'advanced', 'expert'];

function UserGradeAdminSection() {
    const { t } = useTranslation();
    const [users, setUsers] = useState(null);
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const r = await listUserGrades();
            setUsers(r.users || []);
        } catch (err) { toast.error(formatApiError(err, t)); }
    }, [t]);
    useEffect(() => { refresh(); }, [refresh]);

    const change = async (u, grade) => {
        setBusy(u.usrid);
        try {
            const r = await setUserGrade(u.usrid, grade);
            setUsers(list => list.map(x => (x.usrid === u.usrid ? { ...x, grade: r.grade, stored: grade } : x)));
            toast.success(t('settings:grade.saved', { name: u.name || u.usrid, grade: t(`settings:grade.name.${r.grade}`) }));
        } catch (err) {
            toast.error(formatApiError(err, t));
        } finally { setBusy(null); }
    };

    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!users) return [];
        return s ? users.filter(u => [u.name, u.login, u.usrid].some(v => String(v || '').toLowerCase().includes(s))) : users;
    }, [users, q]);
    const counts = useMemo(() => {
        const c = { beginner: 0, advanced: 0, expert: 0 };
        (users || []).forEach(u => { c[u.grade] = (c[u.grade] || 0) + 1; });
        return c;
    }, [users]);

    if (!users) return <div className="stg-row"><span className="stg-hint">{t('settings:grade.loading')}</span></div>;

    return (
        <section>
            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('settings:grade.label')}</span>
                    <span className="stg-hint">{t('settings:grade.hint')}</span>
                </div>
                <button type="button" className="app-btn" onClick={refresh}>
                    <FiRefreshCw size={12} /> {t('settings:grade.refresh')}
                </button>
            </div>
            <ul className="stg-hint stg-list">
                {GRADES.map(g => (
                    <li key={g}><b>{t(`settings:grade.name.${g}`)}</b> ({counts[g]}) — {t(`settings:grade.desc.${g}`)}</li>
                ))}
            </ul>
            <div className="stg-row">
                <input
                    className="stg-text stg-text-wide" value={q}
                    placeholder={t('settings:grade.search')} aria-label={t('settings:grade.search')}
                    onChange={(e) => setQ(e.target.value)}
                />
            </div>
            <div className="stg-table-wrap">
                <table className="stg-table">
                    <thead>
                        <tr>
                            <th className="stg-left">{t('settings:grade.col.user')}</th>
                            <th className="stg-left">{t('settings:grade.col.lastSeen')}</th>
                            <th>{t('settings:grade.col.grade')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map(u => {
                            const fixed = u.admin || u.paid;
                            return (
                                <tr key={u.usrid}>
                                    <td className="stg-left">
                                        {u.name || u.usrid}
                                        <span className="stg-hint"> · {u.login ? `${u.login} · ` : ''}{u.usrid}</span>
                                    </td>
                                    <td className="stg-left stg-hint">{u.lastSeen ? storedTime(u.lastSeen) : '—'}</td>
                                    <td className="stg-center">
                                        {fixed ? (
                                            <span className="stg-hint" title={t(u.admin ? 'settings:grade.adminFixed' : 'settings:grade.paidFixed')}>
                                                {t('settings:grade.name.expert')} ({t(u.admin ? 'settings:grade.admin' : 'settings:grade.paid')})
                                            </span>
                                        ) : (
                                            <select
                                                className="stg-select" value={u.grade} disabled={busy === u.usrid}
                                                aria-label={t('settings:grade.col.grade')}
                                                onChange={(e) => change(u, e.target.value)}
                                            >
                                                {GRADES.map(g => <option key={g} value={g}>{t(`settings:grade.name.${g}`)}</option>)}
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {shown.length === 0 && (
                            <tr><td colSpan={3} className="stg-hint">{t('settings:grade.empty')}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default UserGradeAdminSection;
